
// Author: Çağrı Erdem, 2023
// Description:  playback script for the 2groove web app.

// +-------------------------------+-----------------------------------+
// | playback.js                   | playbackWorker.js                 |
// +-------------------------------+-----------------------------------+
// | 1. Imports modules            | 1. Imports modules                |
// | 2. Set up worker              |                                   |
// | 3. Global setup (vars, etc.)  | 2. Global setup (vars, etc.)      |
// | 4. Event listeners for UI     |                                   |
// | 5. checkAndEnqueueMidi()      |                                   |
// |   - Fetch MIDI data           |                                   |
// |   - Add to midiQueue          |                                   |
// |   - Send LOAD_MIDI to worker  | 3. onmessage (LOAD_MIDI)          |
// |                               |    - parseAndEnqueueMidi()        |
// |                               |      (Parse MIDI, store in queue) |
// | 6. startPlayback()            | 4. onmessage (START_PLAYBACK)     |
// |   - Set isPlaying = true      |    - Set isPlaying = true         |
// |   - Send START_PLAYBACK to    |    - Set startTime                |
// |     worker                    |    - scheduleNextNote()           |
// |                               |      (Schedule next note)         |
// | 7. Worker sends PLAY_NOTE     | 5. scheduleNextNote()             |
// |   message to playback.js      |    - Post PLAY_NOTE to main       |
// |                               |      thread                       |
// | 8. playNote()                 |                                   |
// |   - Trigger sampler note      |                                   |
// | 9. Animation loop via draw()  |                                   |
// |   - Continue visual updates   |                                   |
// | 10. loopPlayback()            | 6. Worker clears MIDI queue       |
// |   - Send REPLAY_MIDI or       |    if STOP_PLAYBACK is called     |
// |     SCHEDULE_NEXT_MIDI to     |                                   |
// |     worker                    | 7. onmessage (REPLAY_MIDI)        |
// |                               |    - parseAndEnqueueMidi()        |
// |                               |    - scheduleNextNote()           |
// +-------------------------------+-----------------------------------+


// imports
import * as Tone from 'tone';
// import { comp, delay, eq, reverb } from "./efx.js";
import { globalFetch } from './globalFetch.js';
import { getBpm } from './interface.js';
import { samples } from './samples.js';
import { Metronome } from './utils/metronome.js';


// glob var
let audioContext = new (window.AudioContext || window.webkitAudioContext)();
let startTime = null; // Start time for the MIDI playback
let timeouts = []; // Store timeout IDs for scheduled notes
let isPlaying = false; // to track the playback status
let isFirstGroove = false;
let processedMidiData; // Global variable to store the processed MIDI data for download
let lookahead = 25; // Look-ahead interval in milliseconds for polling
let mainVol = 0.8; // Main volume for sampler playback
let elapsedTime = 0; // Initialize elapsedTime to track the elapsed time since playback started
let notesInQueue = []; // Queue for notes to be drawn
let lastNoteDrawn = null; // Track the last note drawn
let totalTimeMs = 0; // Initialize totalTimeMs as a global variable
let currentMidiForVisuals = null; // Track the current MIDI for visual playback
let midiQueue = []; // Queue for MIDI files to be played back
let currentPlaybackState = null; // for draw() to access the playback state within worker

// Initialize the metronome
let globalBPM = 90; //TODO: Estimated bpm ilk playback'e ulasmiyo, bunu hallet!!!!
// Create a new Metronome instance with the initial tempo value
const metronome = new Metronome(globalBPM, uiCallback);
metronome.audioContext = audioContext; // Use the same AudioContext as the rest of the app


// Web Worker setup for MIDI processing
const playbackWorker = new Worker(new URL('./workers/playbackWorker.js', import.meta.url), { type: 'module' });
playbackWorker.onmessage = (event) => {
    const { type, data } = event.data;

    switch (type) {
        case 'PLAY_NOTE':
            playNote(data); // Play the note received from the worker
            break;

        case 'MIDI_LOADED':
            console.log("MIDI data successfully loaded in worker");
            break;

        case 'PLAYBACK_COMPLETE':
            isPlaying = false;
            // console.log('[Main] Playback complete.');
            // If there's new MIDI in the queue, initiate playback
            if (midiQueue.length > 0) {
                startPlayback();
            } else {
                loopPlayback(); // Loop the current MIDI if no new MIDI is available --bunu worker'da hallediyoruz sanirim
                // console.log('[Main] No new MIDI in queue, waiting...');
            }
            break;

            case 'PLAYBACK_STATE':
                // Handle the state information for synchronization ---deniyoruz bunu su an
                currentPlaybackState = data;  // Store the state for use in draw()
                break;

        default:
            console.error(`[Main] Unknown message type received from worker: ${type}`);
    }
};


// Check for new MIDI data and enqueue it
function checkAndEnqueueMidi() {
    if (globalFetch.isMidiReadyForProcessing()) {
        const midi = globalFetch.getMidiData().buffer; // Convert to ArrayBuffer when fetching JSON
        globalBPM = getBpm(); // Get the new tempo value from the UI
        metronome.tempo = globalBPM; // Update the metronome tempo
        console.log('Current tempo:', globalBPM);

        if (!midi) {
            console.error('Failed to retrieve MIDI data.');
            return;
        }
        globalFetch.midiProcessed(); // Mark the MIDI as processed
        
        // Add MIDI to the queue
        midiQueue.push(midi);

        // Send MIDI data to the worker for processing
        playbackWorker.postMessage({ type: 'LOAD_MIDI', data: midi, isReplay: false, bpm: globalBPM });
        console.log('Sent LOAD_MIDI message to worker:', { type: 'LOAD_MIDI', data: midi, bpm: globalBPM });

        // If nothing is currently playing, start the next MIDI
        if (!isPlaying) {
            startPlayback();
        }
    }
}

// Start the playback
function startPlayback() {
    if (isPlaying || midiQueue.length === 0) return; // Prevent multiple playback starts
    isPlaying = true;
    startTime = audioContext.currentTime;

    startMetronome(); // Start the metronome without waiting for the groove generation
    forceMetronomeAlign(); // Align the metronome with the groove start time
    metronome.mute = true;

    // Get the next MIDI from the queue for playback
    if (midiQueue.length > 0) {
        lastNoteDrawn = -1; // Reset lastNoteDrawn for the new MIDI playback
        currentMidiForVisuals = midiQueue.shift();
        playbackWorker.postMessage({ type: 'START_PLAYBACK', data: currentMidiForVisuals});
        console.log('Message sent to worker:', { type: 'START_PLAYBACK'});

        requestAnimationFrame(draw); // Start the animation loop for visual updates
    } else {
        console.error('MIDI queue is empty. Cannot start playback.');
    }
}

// Stop the playback
function stopPlayback() {
    if (!isPlaying) return; // Prevent stopping if not playing
    isPlaying = false;
    playbackWorker.postMessage({ type: 'STOP_PLAYBACK' });
    console.log('Sent STOP_PLAYBACK message to worker:', { type: 'STOP_PLAYBACK' });
    resetPlayback();
}

// Reset playback system
function resetPlayback() {
    timeouts.forEach(clearTimeout);
    timeouts = [];
    startTime = null;
    metronome.reset();
    isPlaying = false;
    elapsedTime = 0;
    notesInQueue = [];
    lastNoteDrawn = null;
}

// Play a note triggered by the worker
function playNote(currentNote) {
    if (!currentNote || !currentNote.note) {
        console.error('Invalid note data received:', currentNote);
        return;
    }
    // console.log('Playing note:', currentNote); // Debugging statement

    const velocity = currentNote.velocity;
    const duration = currentNote.duration;
    const noteName = Tone.Frequency(currentNote.note - 12, "midi").toNote();
    const drumType = getDrumType(noteName);

    if (drumType) {
        if (isListening) {
            const noteToTrigger = mapVelocityToSample(velocity);
            const sampler = samplers[drumType];

            sampler.volume.value = scaleValue(mainVol, [0, 1], [-20, 0]);
            // sampler.connect(comp);
            // sampler.connect(eq);
            // sampler.connect(delay);
            // sampler.connect(reverb);
            sampler.triggerAttackRelease(noteToTrigger, duration);
        }
    }
}

// Function to get the drum type from a note name
function getDrumType(noteName) {
    return drumMappings[noteName] || null;
}

// Function to scale velocity
function mapVelocityToSample(velocity) {
    const numberOfSamples = 12;
    const segment = Math.ceil(128 / numberOfSamples);
    const sampleIndex = Math.ceil(velocity / segment);
    const noteNumber = 60 + sampleIndex - 1;
    return Tone.Frequency(noteNumber, "midi").toNote();
}

// Sampler setup using imported samples
const drumParts = ['closed-hihat', 'open-hihat', 'crash', 'ride', 'kick', 'snare', 'hi-tom', 'mid-tom', 'floor-tom'];
const samplers = {};
drumParts.forEach(drum => {
    const drumSamples = {};
    samples[drum].forEach((samplePath, index) => {
        const noteName = Tone.Frequency(60 + index, "midi").toNote();
        drumSamples[noteName] = samplePath;
    });
    samplers[drum] = new Tone.Sampler({
        urls: drumSamples
    }).toDestination();
});

// Drum mappings
const drumMappings = {
    'C1': 'kick',
    'D1': 'snare',
    'F1': 'floor-tom',
    'B1': 'mid-tom',
    'C2': 'hi-tom',
    'C#2': 'crash',
    'D#2': 'ride',
    'F#1': 'closed-hihat',
    'A#1': 'open-hihat'
};



function draw() {
    if (!isPlaying) return;

    let currentTime = audioContext.currentTime;
    elapsedTime = currentTime - startTime; // Update elapsed time
    
    let newNoteToDraw = false;
    
    // Process notes in the queue to find the ones that need to be drawn
    while (notesInQueue.length > 0 && notesInQueue[0].time <= currentTime) {
        const currentNote = notesInQueue.shift();
        playNote(currentNote);

        // Check if this note is different from the last one drawn
        if (currentNote.note !== lastNoteDrawn) {
            newNoteToDraw = true;
            lastNoteDrawn = currentNote.note; // Update the lastNoteDrawn
        }
    }

    // Check if the total playback time has been reached
    if (elapsedTime * 1000 >= totalTimeMs) { // totalTimeMs or loopDurationMs
        loopPlayback(); // Transition to next MIDI or loop
    } else {
        // Continue the animation loop if playback is active
        requestAnimationFrame(draw);
    }
}



// function loopPlayback() {
//     isPlaying = false; // Reset the flag once the playback finishes

//     if (midiQueue.length > 0) {
//         // If there's another MIDI in the queue, play it
//         metronome.reset(); // Reset the metronome's beat counter for the new loop
//         playbackWorker.postMessage({ type: 'LOAD_MIDI', data: midiQueue.shift(), isReplay: false }); // Load next MIDI in worker
//         playbackWorker.postMessage({ type: 'START_PLAYBACK' }); // Start playback after loading
//         isPlaying = true; // Set flag to indicate ongoing playback
//     } else if (currentMidiForVisuals) {
//         // If there's no new MIDI in the queue, reload and replay the current one
//         playbackWorker.postMessage({ type: 'LOAD_MIDI', data: currentMidiForVisuals, isReplay: true }); // Reload MIDI into worker for replay
//         playbackWorker.postMessage({ type: 'START_PLAYBACK' }); // Start playback after reloading
//         isPlaying = true; // Set flag to indicate ongoing playback
//     }
// }


// Loop the MIDI playback
function loopPlayback() {
    isPlaying = false; // Reset the flag once the playback finishes

    // Check if there's another MIDI in the queue and play it
    if (midiQueue.length > 0) {
        startPlayback(); // Use startPlayback() to properly handle queue and playback state
    } else {
        // If there's no new MIDI in the queue, replay the current one
        playbackWorker.postMessage({ type: 'REPLAY_MIDI', data: currentMidiForVisuals });
    }
}




// The polling mechanism in this main module is responsible for fetching new MIDI data and adding it to the queue.
setInterval(() => {
    checkAndEnqueueMidi(); // Check for new MIDI data and add it to the queue

    if (!isPlaying && midiQueue.length > 0) {
        startPlayback(); // Start playback if not already playing and there are MIDI files in the queue
    }
}, lookahead); // Adjusted to check every 25 milliseconds //TODO: Check if this is the correct value for the lookahead interval










//////////////////////////////////////////////////////////////
///////////////////// UI Callbacks ///////////////////////////
//////////////////////////////////////////////////////////////

// Variable to keep track of the playback state
let isListening = false;
const toggleListeningCheckbox = document.querySelector('#togglePlaybackButton input[type="checkbox"]');
toggleListeningCheckbox.addEventListener('change', toggleListening);

// Handle checkbox state change
function toggleListening() {
    isListening = toggleListeningCheckbox.checked;  // Update listening state based on checkbox
}


function uiCallback() {
    const lightElement = document.getElementById('tapTempoLight');  // Your light element's ID
    lightElement.classList.add('light-on');  // Turn the light on

    setTimeout(() => {
        lightElement.classList.remove('light-on');  // Turn the light off
    }, lookahead);  // Duration of the light being 'on' is also 25ms
}


let lastTickTime = null;

function syncLightWithMetronome() {
    if (metronome.isRunning && lastTickTime !== metronome.nextNoteTime) {
        uiCallback();  // Trigger the UI callback if the metronome ticked
        lastTickTime = metronome.nextNoteTime;  // Update the last tick time
    }

    requestAnimationFrame(syncLightWithMetronome);  // Continuously check for metronome ticks
}

syncLightWithMetronome();  // Start syncing the light with the metronome
metronome.tempo = globalBPM; // Update the metronome tempo


//////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////

function resetPlaybackSystem() {
    console.log('Resetting playback system...');

    // 1. Stop all audio processes in the audio context
    if (audioContext.state !== 'closed') {
        audioContext.close().then(() => {
            console.log('AudioContext closed.');

            // Reinitialize the audio context to start fresh
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
            Tone.setContext(audioContext); // Ensure Tone.js uses the new context
            playbackWorker.postMessage({ type: 'STOP_PLAYBACK' }); // Stop playback in the worker

            // Set the new audio context to the metronome
            metronome.setAudioContext(audioContext);

            // Reset other components
            resetMetronome();
            resetMidiQueue();
            resetSamplers();
        }).catch((error) => {
            console.error('Error closing AudioContext:', error);
        });
    } else {
        // If the context was already closed, create a new one directly
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        Tone.setContext(audioContext); // Ensure Tone.js uses the new context

        // Set the new audio context to the metronome
        metronome.setAudioContext(audioContext);

        resetMetronome();
        resetMidiQueue();
        resetSamplers();
    }
}


function resetMetronome() {
    if (metronome.isRunning) {
        metronome.stop();
    }

    // Set the new audio context to the metronome
    metronome.setAudioContext(audioContext);

    // Reset other parameters
    metronome.reset();
    metronome.tempo = globalBPM; // Set the tempo slider value to metronome
    metronome.mute = false; // Start unmuted for the new session
    console.log('Metronome reset.');
}


// Function to clear the MIDI queue and playback state
function resetMidiQueue() {
    midiQueue.length = 0;
    notesInQueue.length = 0;
    timeouts.forEach(clearTimeout);
    timeouts = [];
    isPlaying = false;
    startTime = null;
    lastNoteDrawn = -1;
    isFirstGroove = true;
    console.log('MIDI queue and scheduled notes cleared.');
}

// Function to reset samplers
function resetSamplers() {
    Object.values(samplers).forEach(sampler => {
        sampler.disconnect();
        sampler.toDestination();
    });
    console.log('Samplers reset.');
}


// Utility function to scale a value from one range to another
function scaleValue(value, from, to) {
    let scale = (to[1] - to[0]) / (from[1] - from[0]);
    let capped = Math.min(from[1], Math.max(from[0], value)) - from[0];
    return (capped * scale + to[0]);
}

// Function to randomize value between a range
function randomizeValue(min, max, isInteger) {
    const randomValue = Math.random() * (max - min) + min;
    return isInteger ? Math.floor(randomValue) : randomValue;
}

// Start the metronome
function startMetronome() {
    if (!metronome.isRunning) {
        metronome.start();
        metronome.reset();
    }
}

// Realign the metronome with the groove start time
function forceMetronomeAlign() {
    const loopStartTime = audioContext.currentTime;
    metronome.alignWithGrooveStart(loopStartTime);
}




// Event listener for the reset button
document.getElementById('resetStudy').addEventListener('click', resetPlaybackSystem);
document.getElementById('nextSessionBtn').addEventListener('click', resetPlaybackSystem);
document.getElementById('cleanCanvasBtn').addEventListener('click', resetPlaybackSystem);
